import { useCore } from "@hooks/useCore";
import { useLocalisationContext } from "@hooks/useLocalisation";
import { useShopify } from "@hooks/useShopify";
import { NormalisedProduct, ProductStub } from "@ts/components";
import React, { Context, ReactNode, createContext, useMemo } from "react";

import { useStorefrontQuery } from "~/hooks/useStorefront";

type ProductProviderProps = {
  children: ReactNode;
  queryType?: "PDP" | "SUMMARY";
  stub?: ProductStub;
};

export type ProductContext = {
  loading: boolean;
  product: NormalisedProduct;
};

export const ProductContext: Context<ProductContext> =
  createContext<ProductContext>({});

export const ProductProvider = ({
  children,
  stub,
  queryType = "SUMMARY",
}: ProductProviderProps) => {
  const {
    graphql: {
      queries: { GET_PRODUCT, GET_PRODUCT_WITH_RECOMMENDATIONS },
    },
  } = useCore();
  const { contextCountry } = useLocalisationContext();
  const {
    encodeProductId,
    edgeNormaliser,
    parseShopifyRaw,
    productNormaliser,
  } = useShopify();
  const normalisedStub = productNormaliser({
    images: [{ src: stub?.image }],
    handle: stub?.shopify?.handle ?? stub?.shopify?.shopifyHandle,
    id: stub?.shopify?.id ?? stub?.shopify?.shopifyId,
    ...parseShopifyRaw(stub),
  });
  const { id, handle } = normalisedStub;
  const queries = {
    PDP: {
      query: GET_PRODUCT_WITH_RECOMMENDATIONS,
    },
    SUMMARY: {
      query: GET_PRODUCT,
    },
  };
  const { query } = queries[queryType];

  const { data, loading } = useStorefrontQuery(query, {
    variables: {
      countryCode: contextCountry,
      id: encodeProductId(id as string),
      handle,
      firstImages: 10,
      firstMedia: 1,
      firstVariants: 50,
      firstCollections: 10,
      firstMetafields: 10,
    },
  });
  const product =
    !loading && data?.product
      ? { ...normalisedStub, ...productNormaliser(data?.product) }
      : normalisedStub;

  const values = useMemo(
    () => ({
      loading,
      product,
    }),
    [loading, product],
  );

  return (
    <ProductContext.Provider value={values}>{children}</ProductContext.Provider>
  );
};
